import { Cookie } from "../common/cookie";
import { AtlassianIdAuth } from "../common/xsrf-token";

export type Product = {
    readonly product: string;
    readonly accountName?: string;
    readonly edition?: string;
};

export type ProductActivation = {
    readonly productKey: string;
    readonly edition?: string;
};

export type Experiment = {
    readonly experimentKey: string;
    readonly experimentAttribute: string;
};

export type AdvancedSettings = {
    readonly completionCallbackAudience?: string;
    readonly completionCallbackURL?: string;
    readonly originatorIP?: string;
    readonly suppressIdentityEmail?: boolean;
    readonly suppressEmail?: boolean;
    readonly evalDays?: number;
    readonly additionalProvisioningData?: unknown;
    readonly initialProductURL?: string;
};

export type ConsentItem = {
    readonly key: string;
    readonly displayedText: string;
    readonly granted: boolean;
};

export type Consent = {
    readonly site: string | null;
    readonly locale: string | null;
    readonly formUrl: string | null;
    readonly consents: ConsentItem[];
};

export type ActivateProductsRequestBody = {
    readonly products: ProductActivation[];
    readonly advancedSettings: {
        additionalProvisioningData: {
            actor: string;
            workspaceUuid: string;
        };
    };
};

export type CreateCloudRequestBody = {
    readonly cloudName: string;
    readonly email: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly products: Product[];

    readonly adminPassword?: string;
    readonly adminUsername?: string;
    readonly country?: string;
    readonly state?: string;
    readonly timezone?: string;
    readonly coupon?: string;
    readonly experiment?: Experiment;
    readonly advancedSettings?: AdvancedSettings;
    readonly signupContext?: string;
    readonly testingPurposes?: boolean;
    readonly consent?: Consent;
    readonly languagePreference?: string;
};

export type CreateCloudRequestAnalyticsData = Cookie[];

export type BaseRequest = {
    readonly activateProducts?: boolean;

    readonly source?: string;
    readonly clientIp?: string;
    readonly userContextToken?: string;
};

export type CreateCloudRequest = {
    readonly body: CreateCloudRequestBody;
    readonly auth?: AtlassianIdAuth;
    readonly analyticsData?: CreateCloudRequestAnalyticsData;
} & BaseRequest;

export type ActivateProductsRequest = {
    readonly cloudId: string;
    readonly body: ActivateProductsRequestBody;
    readonly auth?: AtlassianIdAuth;
    readonly consent?: Consent;

    readonly analyticsData?: CreateCloudRequestAnalyticsData;
} & BaseRequest;

export type CreateCloudResponse = {
    readonly cloudId: string;
    readonly progressUri: string;
};

export type ActivateProductsResponse = {
    readonly progressUri: string;
};

export type GetNameAvailabilityOptions = {
    readonly name: string;
};

export type GetNameAvailabilityResponseBody = {
    readonly message: string;
    readonly success: boolean;
};

export type ReserveNameOptions = {
    readonly cloudName: string;
};

export type ReserveNameResponseBody = {
    readonly token: string;
    readonly validUntil: string;
};

export type ResourcePackagesCheckOptions = {
    readonly entitlements: string[];
};

export type ResourcePackagesCheckResponseBody = {
    readonly [key: string]: {
        readonly status: string;
        readonly resourceAris: string[];
    };
};

export type RenameSiteOptions = {
    readonly cloudId: string;
    readonly newSiteName: string;
};

export type RenameSiteResponseBody = {
    readonly progressUri: string;
};

export type GetCloudStatusOptions = {
    readonly cloudId: string;
    readonly requestId: string;
};

export type CloudStatusResponse = {
    readonly cloudId: string;
    readonly requestId: string;
    readonly completed: boolean;
    readonly failureReason: Readonly<Record<string, string>>;
    readonly requestType: string;
    readonly successful: boolean;
    readonly billingDetails: BillingDetailsType;
    readonly bestStatuses: Readonly<Record<string, string>>;
};

export type BillingDetailsType = {
    readonly sen: string;
    readonly startDate: string;
    readonly endDate: string;
    readonly childAccounts: Array<AccountType>;
};

export type AccountType = {
    readonly productKey: string;
    readonly ccpEntitlementId: string;
    readonly slug: string;
};

export type PollProgressOptions = {
    readonly progressUri: string;
};

export type PollProgressResponseBody = {
    readonly cloudId: string;
    readonly requestId: number;
    readonly requestType: string;
    readonly completed: boolean;
    readonly successful: boolean;
    readonly billingDetails: { [key: string]: string };
    readonly bestStatuses: { [key: string]: string };
    readonly failureReason: { [key: string]: unknown };
};
